import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import type { JourneyBookEntry, JourneyEntryInputDraft } from '@jupi/shared'
import { ApiError, formatDate, getClubTimezone, useAuth, useSnackbar } from '@jupi/ui'
import { useBookMaintenanceRecords } from '../../api/flightLog'
import { draftOf, draftProblem, newDraft, toInput, type EntryDraft } from './journeyEntryForm'

const number = (value: string) => (value === '' ? 0 : Number(value))

// Adds or corrects a row of the paper book that is not a flight: an
// engineer's entry (text typed as the paper has it, optionally linked to a
// maintenance record, which never fills the text in) or a run of blank rows.
// "Blank rows before / after" keep the paper's exact positioning.
export function JourneyEntryDialog({
  open,
  onClose,
  registration,
  bookNumber,
  rowsPerSpread,
  entry,
  onSave,
  saving,
}: {
  open: boolean
  onClose: () => void
  registration: string
  bookNumber: number
  rowsPerSpread: number | null
  // The row being corrected; none for a new one.
  entry?: JourneyBookEntry
  onSave: (body: JourneyEntryInputDraft) => Promise<unknown>
  saving: boolean
}) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const { user } = useAuth()
  const timeZone = getClubTimezone()
  const [draft, setDraft] = useState<EntryDraft>(() =>
    entry ? draftOf(entry, timeZone) : newDraft(new Date(), timeZone, user?.displayName ?? ''),
  )
  const { data: records = [] } = useBookMaintenanceRecords(registration, bookNumber, open && draft.kind === 'entry')
  const set = (patch: Partial<EntryDraft>) => setDraft((d) => ({ ...d, ...patch }))
  const body = toInput(draft, timeZone, rowsPerSpread)
  const editing = entry !== undefined
  const rowsField = { htmlInput: { min: 0, max: rowsPerSpread ?? 100, inputMode: 'numeric' as const } }

  const submit = async () => {
    if (!body) return
    try {
      await onSave(body)
      onClose()
    } catch (err) {
      showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t(editing ? 'flightLog.entryEdit' : 'flightLog.entryAdd')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {editing ? null : (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={draft.kind}
              onChange={(_, kind) => kind && set({ kind })}
              aria-label={t('flightLog.entryKind')}
            >
              <ToggleButton value="entry">{t('flightLog.entryKindEntry')}</ToggleButton>
              <ToggleButton value="blank">{t('flightLog.entryKindBlank')}</ToggleButton>
            </ToggleButtonGroup>
          )}
          <Stack direction="row" spacing={2}>
            <TextField
              required
              type="date"
              label={t('flightLog.entryDate')}
              value={draft.date}
              onChange={(e) => set({ date: e.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              required
              type="time"
              label={t('flightLog.entryTime')}
              value={draft.time}
              onChange={(e) => set({ time: e.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText={t('flightLog.entryWhenHint')}
            />
          </Stack>
          {draft.kind === 'entry' ? (
            <>
              <TextField
                required
                multiline
                minRows={4}
                label={t('flightLog.entryText')}
                helperText={t('flightLog.entryTextHint')}
                value={draft.text}
                onChange={(e) => set({ text: e.target.value })}
                slotProps={{ htmlInput: { maxLength: 4000 } }}
              />
              <TextField
                label={t('flightLog.entryEnteredBy')}
                helperText={t('flightLog.entryEnteredByHint')}
                value={draft.enteredByText}
                onChange={(e) => set({ enteredByText: e.target.value })}
                slotProps={{ htmlInput: { maxLength: 200 } }}
              />
              <TextField
                select
                label={t('flightLog.entryRecord')}
                helperText={t('flightLog.entryRecordHint')}
                value={draft.maintenanceRecordId ?? ''}
                onChange={(e) => set({ maintenanceRecordId: e.target.value === '' ? null : Number(e.target.value) })}
              >
                <MenuItem value="">{t('common.none')}</MenuItem>
                {records.map((r) => (
                  <MenuItem key={r.recordId} value={r.recordId}>
                    {formatDate(r.performedOn)} · {r.maintenanceType}
                    {r.performedBy ? ` · ${r.performedBy}` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </>
          ) : null}
          <TextField
            type="number"
            label={t(draft.kind === 'entry' ? 'flightLog.entryRows' : 'flightLog.entryBlankRows')}
            helperText={t('flightLog.entryRowsHint')}
            value={draft.rowCount}
            onChange={(e) => set({ rowCount: number(e.target.value) })}
            slotProps={{ htmlInput: { ...rowsField.htmlInput, min: 1 } }}
          />
          {draft.kind === 'entry' ? (
            <Stack direction="row" spacing={2}>
              <TextField
                type="number"
                label={t('flightLog.entryBlankBefore')}
                value={draft.blankBefore}
                onChange={(e) => set({ blankBefore: number(e.target.value) })}
                slotProps={rowsField}
              />
              <TextField
                type="number"
                label={t('flightLog.entryBlankAfter')}
                value={draft.blankAfter}
                onChange={(e) => set({ blankAfter: number(e.target.value) })}
                slotProps={rowsField}
              />
            </Stack>
          ) : null}
          {draftProblem(draft, rowsPerSpread) === 'rows' ? (
            <Typography variant="body2" color="error">
              {t('flightLog.entryRowsError', { max: rowsPerSpread ?? 100 })}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!body || saving} onClick={submit}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
